/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

// @mui material components
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import TableHeader from 'components/Table/TableHeader';
import MDDateRange from 'components/MDDateRange';

// Material Dashboard 2 PRO React examples
import Navbar from 'examples/Navbars/NavBar';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import * as Func from 'services/Function';

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  page: 1,
  perPage: 10,
  pageTotal: 0,
  period: 'ALL',
  branch: 'ALL',
  status: 'success',
  date_start: Func.dateTimeFirstMonth(),
  date_stop: Func.dateTimeEndMonth(),
};

const initSummary = {
  quantity: '',
  total: '',
  nettotal: '',
  vat: '',
  discount_all: '',
};

function RpSaleOrder() {
  const mod = 'rp_sale';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [find, setFind] = useState(initFind);
  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(initFind.order_sort);
  const [orderBy, setOrderBy] = useState(initFind.order_by);
  const [cboBranch, setCboBranch] = useState([]);
  const [cboStatus, setCboStatus] = useState([]);
  const [summary, setSummary] = useState(initSummary);
  const [urlExcel, setUrlExcel] = useState('');

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const initHeader = [
    { id: 'tb.status', label: 'สถานะ', align: 'center' },
    { id: 'tb.id', label: t(`attr.code`), align: 'center' },
    { id: 'cs.code', label: 'รหัสรอบ', align: 'center' },
    { id: 'cs.date_record', label: 'รอบวันที่', align: 'center' },
    { id: 'tb.client_name', label: 'ลูกค้า', align: 'left' },
    { id: 'tb.branch_id', label: 'สาขา', align: 'left' },
    { id: 'tb.user_id', label: 'ผู้ทำรายการ', align: 'left' },
    { id: 'sales_user_name', label: 'พนักงานขาย', align: 'left' },
    { id: 'tb.total', label: 'มูลค่า', align: 'right' },
    { id: 'tb.discount_all', label: 'ส่วนลด', align: 'right' },
    { id: 'tb.nettotal', label: 'สุทธิ', align: 'right' },
    { id: 'tb.payment_type', label: 'การชำระ', align: 'center' },
    { id: 'tb.created_at', label: 'วันที่', align: 'center' },
  ];

  const view = (data) => {
    setFind(data);
    setLoading(true);
    setRecord([]);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      branch: data.branch,
      period: data.period,
      date_start: data.date_start,
      date_stop: data.date_stop,
      status: data.status,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);

        setPageTotal(result.total_pages);
        setPageNo(result.page);

        setSummary(result.summary);
      }
      setLoading(false);
    });
  };

  const setDateRange = (start, stop) => {
    view({
      ...find,
      date_start: Func.dateTimeFormat(start.toDate()),
      date_stop: Func.dateTimeFormat(stop.toDate()),
      page: 1,
    });
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 10 })}>
        {t(`element.show_record`, { num: 10 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 20 })}>
        {t(`element.show_record`, { num: 20 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 100 })}>
        {t(`element.show_record`, { num: 100 })}
      </MenuItem>
    </Menu>
  );

  const handleSort = (e, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);

    view({ ...find, order_by: property, order_sort: isAsc ? 'desc' : 'asc' });
  };

  const init = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboBranch(result.branch);
        setCboStatus(result.stepper);
        setUrlExcel(result.excel);
        view(find);
      } else {
        Func.exitApp();
      }
    });
  };

  const excel = () => {
    let param = `download`;
    param += `?search=${find.search}`;
    param += `&branch=${find.branch}`;
    param += `&period=${find.period}`;
    param += `&status=${find.status}`;
    param += find.period === 'ALL' ? '' : `&date_start=${find.date_start}`;
    param += find.period === 'ALL' ? '' : `&date_stop=${find.date_stop}`;

    window.open(`${urlExcel}/${param}`, '_self');
    // window.open(`${urlExcel}/${param}`, "_blank");
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <MDInput
              fullWidth
              value={find.branch}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, branch: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>{t('menu.branch')} :</InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboBranch.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.status}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, status: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>สถานะ :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboStatus.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>

          {find.period === 'ALL' ? (
            <Grid item xs={12} sm={12} md={6} lg={2}>
              <MDButton
                variant='outlined'
                color='secondary'
                fullWidth
                sx={{ p: 1.5 }}
                onClick={() => view({ ...find, period: 'DATE', page: 1 })}
              >
                <Icon>edit_calendar</Icon>&nbsp;เลือกช่วงเวลา
              </MDButton>
            </Grid>
          ) : null}
          {find.period === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={12} lg={7}>
              <MDDateRange
                dateStart={find.date_start}
                dateStop={find.date_stop}
                callBack={setDateRange}
                closeDate={() => view({ ...find, period: 'ALL', page: 1 })}
              />
            </Grid>
          )}
          {find.period === 'ALL' ? null : (
            <Grid item xs={12} sm={12} md={12} lg={5}>
              <MDBox display='flex' justifyContent='end' gap={1}>
                <MDButton variant='gradient' color='success' onClick={excel}>
                  <Icon>table_view</Icon>&nbsp;Excel
                </MDButton>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3} md={2} lg={1}>
            <MDButton
              variant='contained'
              color='dark'
              size='large'
              fullWidth
              sx={{ pt: 1.6, pb: 1.6 }}
              href='/report_pos'
            >
              <Icon>content_paste_search</Icon>
            </MDButton>
          </Grid>
          <Grid item xs={12} sm={9} md={10} lg={11}>
            {renderSearch()}
          </Grid>
          <Grid item xs={12}>
            <TableContainer component={Paper} sx={{ minHeight: 400 }}>
              <Table aria-label='datatable table'>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleSort}
                  data={initHeader}
                />
                <TableBody>
                  {record.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell align='center' width={160}>
                        {Func.statusDisplay(row)}
                      </TableCell>
                      <TableCell align='center'>{row.code}</TableCell>
                      <TableCell align='center' className='nowrap'>
                        {row.shift_code}
                      </TableCell>
                      <TableCell align='center'>{Func.dateDisplay(row.shift_date)}</TableCell>
                      <TableCell>
                        <MDBox className='nowrap'>{row.client_name}</MDBox>
                        <MDBox className='nowrap'>{row.country_name}</MDBox>
                      </TableCell>
                      <TableCell align='left' className='nowrap'>
                        {row.branch_name}
                      </TableCell>
                      <TableCell align='left'>{row.user_name}</TableCell>
                      <TableCell align='left'>{row.sales_user_name || '-'}</TableCell>
                      <TableCell align='right' className='color-info'>
                        {Func.numberDisplay(row.total, 2)}
                      </TableCell>
                      <TableCell align='right' className='color-danger'>
                        {Func.numberDisplay(row.discount_all, 2)}
                      </TableCell>
                      <TableCell align='right' className='color-success'>
                        {Func.numberDisplay(row.nettotal, 2)}
                      </TableCell>
                      <TableCell align='center'>{row.payment_name}</TableCell>
                      <TableCell align='center' className='nowrap'>
                        {Func.dateTimeDisplay(row.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell align='right' colSpan={8}>
                      รวม
                    </TableCell>
                    <TableCell align='right' className='color-info'>
                      {Func.numberDisplay(summary.total)}
                    </TableCell>
                    <TableCell align='right' className='color-danger'>
                      {Func.numberDisplay(summary.discount_all)}
                    </TableCell>
                    <TableCell align='right' className='color-success'>
                      {Func.numberDisplay(summary.nettotal)}
                    </TableCell>
                    <TableCell colSpan={2} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
          <Grid item xs={12}>
            <Card sx={{ p: 2 }}>
              <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
                <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                  {t(`element.show_record`, { num: find.perPage })}&nbsp;
                  <Icon>keyboard_arrow_down</Icon>
                </MDButton>
                {renderShow}
                <Pagination
                  count={pageTotal}
                  variant='outlined'
                  color='secondary'
                  showFirstButton
                  showLastButton
                  page={pageNo}
                  onChange={(e, p) => view({ ...find, page: p })}
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <MDBox>
      <Navbar route={[t(`menu.report_pos`), t(`menu.rp_sale_order`)]} />
      {renderList()}
      {renderLoading()}
    </MDBox>
  );
}

export default RpSaleOrder;
